import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLocation } from '../contexts/LocationContext';
import { useNotifications } from '../hooks/useNotifications';
import {
  User, Moon, Sun, LogOut, Store, ChevronRight,
  Bookmark, Bell, BellOff, MapPin, MapPinOff,
  Shield, X
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   HOOK — état permission notifications (live)
───────────────────────────────────────────────────────────────── */
const useNotifPermission = () => {
  const getState = () => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  };
  const [state, setState] = useState(getState);

  useEffect(() => {
    const id = setInterval(() => setState(getState()), 1500);
    return () => clearInterval(id);
  }, []);

  return state;
};

/* ─────────────────────────────────────────────────────────────────
   HOOK — état permission géolocalisation (live + fallback probe)
───────────────────────────────────────────────────────────────── */
const useGeoPermission = () => {
  const [state, setState] = useState('checking');

  useEffect(() => {
    let permResult = null;

    const probeViaGPS = () => {
      if (!('geolocation' in navigator)) { setState('denied'); return; }
      navigator.geolocation.getCurrentPosition(
        ()    => setState('granted'),
        (err) => {
          if (err.code === 1) setState('denied');
          else setState('prompt');
        },
        { timeout: 150, maximumAge: Infinity, enableHighAccuracy: false }
      );
    };

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          permResult = result;
          setState(result.state);
          result.onchange = () => setState(result.state);
          if (result.state === 'prompt') probeViaGPS();
        })
        .catch(probeViaGPS);
    } else {
      probeViaGPS();
    }

    const id = setInterval(() => {
      if (permResult) setState(permResult.state);
      else probeViaGPS();
    }, 2000);

    return () => {
      clearInterval(id);
      if (permResult) permResult.onchange = null;
    };
  }, []);

  return state;
};

/* ─────────────────────────────────────────────────────────────────
   MODAL DÉCONNEXION
───────────────────────────────────────────────────────────────── */
const LogoutModal = ({ onConfirm, onCancel, loading }) => (
  <div
    className="logout-overlay"
    onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="logout-title"
  >
    <div className="logout-sheet">
      <div className="logout-sheet__handle" />
      <div className="logout-sheet__icon">
        <LogOut size={26} strokeWidth={1.8} />
      </div>
      <h2 className="logout-sheet__title" id="logout-title">
        Se déconnecter ?
      </h2>
      <p className="logout-sheet__sub">
        Vous devrez vous reconnecter pour accéder à votre compte et gérer votre magasin.
      </p>
      <div className="logout-sheet__actions">
        <button
          className="logout-sheet__confirm"
          onClick={onConfirm}
          disabled={loading}
        >
          <LogOut size={17} strokeWidth={2} />
          {loading ? 'Déconnexion…' : 'Oui, me déconnecter'}
        </button>
        <button
          className="logout-sheet__cancel"
          onClick={onCancel}
          disabled={loading}
        >
          Annuler
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MODAL PERMISSION CUSTOM
   type: 'geo-blocked' | 'geo-active' | 'notif-blocked' | 'notif-active'
───────────────────────────────────────────────────────────────── */
const PERM_MODAL_CONFIG = {
  'geo-blocked': {
    icon:      MapPinOff,
    iconBg:    'rgba(239,68,68,0.10)',
    iconColor: '#ef4444',
    accent:    '#ef4444',
    title:     'Géolocalisation bloquée',
    body:      'Vous avez refusé l\'accès à votre position. Pour l\'activer, autorisez-la manuellement dans les réglages de votre navigateur.',
    steps: [
      { emoji: '🔒', text: 'Appuyez sur l\'icône 🔒 dans la barre d\'adresse' },
      { emoji: '📍', text: 'Sélectionnez « Autoriser » pour la Localisation' },
      { emoji: '🔄', text: 'Rechargez la page' },
    ],
    cta:    null,
    cancel: 'Fermer',
  },
  'geo-active': {
    icon:      MapPin,
    iconBg:    'rgba(0,201,167,0.10)',
    iconColor: '#00c9a7',
    accent:    '#00c9a7',
    title:     'Désactiver la géolocalisation',
    body:      'Il n\'est pas possible de révoquer cette permission depuis l\'application. Vous pouvez la désactiver directement depuis votre navigateur.',
    steps: [
      { emoji: '🔒', text: 'Appuyez sur l\'icône 🔒 dans la barre d\'adresse' },
      { emoji: '📍', text: 'Sélectionnez « Bloquer » pour la Localisation' },
    ],
    cta:    null,
    cancel: 'Compris',
  },
  'notif-blocked': {
    icon:      BellOff,
    iconBg:    'rgba(239,68,68,0.10)',
    iconColor: '#ef4444',
    accent:    '#ef4444',
    title:     'Notifications bloquées',
    body:      'Vous avez refusé les notifications. Pour les réactiver, autorisez-les dans les réglages de votre navigateur.',
    steps: [
      { emoji: '🔒', text: 'Appuyez sur l\'icône 🔒 dans la barre d\'adresse' },
      { emoji: '🔔', text: 'Sélectionnez « Autoriser » pour les Notifications' },
      { emoji: '🔄', text: 'Rechargez la page' },
    ],
    cta:    null,
    cancel: 'Fermer',
  },
  'notif-active': {
    icon:      Bell,
    iconBg:    'rgba(0,201,167,0.10)',
    iconColor: '#00c9a7',
    accent:    '#00c9a7',
    title:     'Désactiver les notifications',
    body:      'Il n\'est pas possible de révoquer cette permission depuis l\'application. Vous pouvez les désactiver depuis votre navigateur.',
    steps: [
      { emoji: '🔒', text: 'Appuyez sur l\'icône 🔒 dans la barre d\'adresse' },
      { emoji: '🔔', text: 'Sélectionnez « Bloquer » pour les Notifications' },
    ],
    cta:    null,
    cancel: 'Compris',
  },
};

const PermissionModal = ({ type, onClose }) => {
  const cfg = PERM_MODAL_CONFIG[type];
  if (!cfg) return null;
  const Icon = cfg.icon;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.50)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.25rem',
        animation: 'pmFadeIn 0.16s ease',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div style={{
        background: 'var(--bg)',
        borderRadius: 'var(--r-xl)',
        padding: '1.25rem',
        width: '100%', maxWidth: 340,
        boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
        animation: 'pmPopIn 0.20s cubic-bezier(0.34,1.4,0.64,1)',
      }}>

        {/* Header : icône + titre + close */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div style={{
            width: 42, height: 42, borderRadius: 'var(--r-md)',
            background: cfg.iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon size={22} color={cfg.iconColor} strokeWidth={2}/>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text)', lineHeight: 1.2 }}>
              {cfg.title}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.15rem', fontWeight: 500 }}>
              Permission navigateur
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--bg-elevated)', border: 'none',
              borderRadius: '50%', width: 28, height: 28,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: 'var(--text-3)',
              flexShrink: 0, boxShadow: 'var(--neu-sm)',
            }}
          >
            <X size={13}/>
          </button>
        </div>

        {/* Corps */}
        <p style={{
          fontSize: '0.78rem', color: 'var(--text-2)',
          lineHeight: 1.55, marginBottom: '0.9rem',
        }}>
          {cfg.body}
        </p>

        {/* Étapes compactes */}
        <div style={{
          background: 'var(--bg-elevated)',
          borderRadius: 'var(--r-md)',
          padding: '0.65rem 0.75rem',
          display: 'flex', flexDirection: 'column', gap: '0.5rem',
          marginBottom: '1rem',
          boxShadow: 'var(--neu-in-sm)',
        }}>
          {cfg.steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: `${cfg.accent}18`,
                border: `1.5px solid ${cfg.accent}35`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                fontSize: '0.65rem', fontWeight: 800, color: cfg.accent,
              }}>
                {i + 1}
              </div>
              <span style={{
                fontSize: '0.76rem', color: 'var(--text-2)',
                lineHeight: 1.35, fontWeight: 500,
              }}>
                {step.text}
              </span>
            </div>
          ))}
        </div>

        {/* Bouton */}
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '0.72rem',
            borderRadius: 'var(--r-lg)',
            border: `1.5px solid ${cfg.accent}40`,
            background: `${cfg.accent}12`,
            color: cfg.accent,
            fontWeight: 700, fontSize: '0.87rem',
            cursor: 'pointer', fontFamily: 'inherit',
            transition: 'all 0.15s',
          }}
        >
          {cfg.cancel}
        </button>
      </div>

      <style>{`
        @keyframes pmFadeIn { from { opacity:0 }               to { opacity:1 } }
        @keyframes pmPopIn  { from { opacity:0; transform:scale(.92) } to { opacity:1; transform:scale(1) } }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ROW TOGGLE RÉUTILISABLE
───────────────────────────────────────────────────────────────── */
const ToggleRow = ({ icon: Icon, iconBg, iconColor, title, subtitle, subtitleVariant, checked, onChange, disabled }) => (
  <button
    className="profile-toggle-row"
    onClick={onChange}
    disabled={disabled}
    aria-pressed={checked}
  >
    <div className="profile-toggle-left">
      <div
        className="profile-toggle-icon"
        style={{ background: iconBg, color: iconColor }}
      >
        <Icon size={17} strokeWidth={2} />
      </div>
      <div className="profile-toggle-text">
        <div className="profile-toggle-title">{title}</div>
        <div className={`profile-toggle-sub${subtitleVariant ? ` profile-toggle-sub--${subtitleVariant}` : ''}`}>
          {subtitle}
        </div>
      </div>
    </div>
    <label className="switch" onClick={e => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-label={title}
      />
      <span className="switch-track" />
    </label>
  </button>
);

/* ─────────────────────────────────────────────────────────────────
   PAGE PROFILE
───────────────────────────────────────────────────────────────── */
const Profile = () => {
  const { user, logout, isMerchant, isAdmin }   = useAuth();
  const { theme, toggleTheme }                  = useTheme();
  const { getCurrentPosition }                  = useLocation();
  const { requestPermission }                   = useNotifications();
  const navigate                                = useNavigate();

  const [loggingOut,   setLoggingOut  ] = useState(false);
  const [showLogout,   setShowLogout  ] = useState(false);
  const [geoLoading,   setGeoLoading  ] = useState(false);
  const [permModal,    setPermModal   ] = useState(null); // type du modal permission

  /* Permissions live */
  const notifPerm = useNotifPermission();
  const geoPerm   = useGeoPermission();

  /* ── Initiales avatar ── */
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  /* ── Déconnexion ── */
  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/');
    } finally {
      setLoggingOut(false);
      setShowLogout(false);
    }
  }, [logout, navigate]);

  /* ── Toggle notifications ── */
  const handleNotifToggle = useCallback(async () => {
    if (notifPerm === 'granted') {
      // Impossible de révoquer programmatiquement → modal custom
      setPermModal('notif-active');
      return;
    }
    if (notifPerm === 'denied') {
      // Bloquées → modal custom avec instructions
      setPermModal('notif-blocked');
      return;
    }
    // 'default' → demander la permission native
    await requestPermission();
  }, [notifPerm, requestPermission]);

  /* ── Toggle géolocalisation ── */
  const handleGeoToggle = useCallback(async () => {
    if (geoPerm === 'checking') return;
    if (geoPerm === 'granted') {
      // Impossible de révoquer programmatiquement → modal custom
      setPermModal('geo-active');
      return;
    }
    if (geoPerm === 'denied') {
      // Bloquée → modal custom avec instructions
      setPermModal('geo-blocked');
      return;
    }
    // 'prompt' → popup native
    setGeoLoading(true);
    await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => { getCurrentPosition().catch(() => {}); resolve(); },
        () => resolve(),
        { timeout: 15000, maximumAge: 0, enableHighAccuracy: true }
      );
    });
    setGeoLoading(false);
  }, [geoPerm, getCurrentPosition]);

  /* ── Labels dynamiques ── */
  const notifLabel = () => {
    if (!('Notification' in window)) return { text: 'Non supporté',              variant: 'error'   };
    if (notifPerm === 'granted')     return { text: 'Activées',                  variant: null      };
    if (notifPerm === 'denied')      return { text: 'Bloquées — voir les réglages', variant: 'error' };
    return                                  { text: 'Appuyer pour autoriser',    variant: 'warning' };
  };

  const geoLabel = () => {
    if (!('geolocation' in navigator)) return { text: 'Non supporté',              variant: 'error'   };
    if (geoPerm === 'checking')        return { text: 'Détection en cours…',        variant: null      };
    if (geoPerm === 'granted')         return { text: 'Activée',                    variant: null      };
    if (geoPerm === 'denied')          return { text: 'Bloquée — voir les réglages', variant: 'error' };
    return                                    { text: 'Appuyer pour autoriser',     variant: 'warning' };
  };

  const notif = notifLabel();
  const geo   = geoLabel();

  return (
    <div className="page-enter" style={{ paddingBottom: '2rem' }}>

      {/* ══════ HERO ══════ */}
      <div className="profile-hero">
        <div className="profile-avatar">{initials}</div>

        <div className="profile-hero__name">
          {user ? user.name || user.email : 'Visiteur'}
        </div>

        <div className="profile-hero__role">
          {isMerchant
            ? <><Store   size={12} strokeWidth={2.5} /> Commerçant</>
            : user
              ? <><User    size={12} strokeWidth={2.5} /> Utilisateur</>
              : <><User    size={12} strokeWidth={2.5} /> Visiteur</>
          }
        </div>

        {user?.email && (
          <div className="profile-hero__email">{user.email}</div>
        )}
      </div>

      {/* ══════ CONTENU ══════ */}
      <div className="profile-content">

        {/* ── Apparence ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', margin: 0 }}>
          <div className="profile-section-title">Apparence</div>

          <ToggleRow
            icon={theme === 'dark' ? Sun : Moon}
            iconBg={theme === 'dark' ? 'rgba(99,102,241,.12)' : 'var(--bg)'}
            iconColor={theme === 'dark' ? '#818cf8' : 'var(--text-2)'}
            title="Mode sombre"
            subtitle={theme === 'dark' ? 'Actif' : 'Désactivé'}
            checked={theme === 'dark'}
            onChange={toggleTheme}
          />
        </div>

        {/* ── Confidentialité & permissions ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', margin: 0 }}>
          <div className="profile-section-title">Confidentialité</div>

          {/* Notifications */}
          <ToggleRow
            icon={notifPerm === 'granted' ? Bell : BellOff}
            iconBg={notifPerm === 'granted'
              ? 'rgba(0,201,167,.12)'
              : notifPerm === 'denied'
                ? 'rgba(240,86,74,.10)'
                : 'var(--bg)'}
            iconColor={notifPerm === 'granted'
              ? 'var(--primary)'
              : notifPerm === 'denied'
                ? 'var(--error)'
                : 'var(--text-2)'}
            title="Notifications"
            subtitle={notif.text}
            subtitleVariant={notif.variant}
            checked={notifPerm === 'granted'}
            onChange={handleNotifToggle}
            disabled={!('Notification' in window)}
          />

          <div className="divider" />

          {/* Géolocalisation */}
          <ToggleRow
            icon={geoPerm === 'granted' ? MapPin : MapPinOff}
            iconBg={
              geoPerm === 'granted' ? 'rgba(0,201,167,.12)' :
              geoPerm === 'denied'  ? 'rgba(240,86,74,.10)' :
              'var(--bg)'
            }
            iconColor={
              geoPerm === 'granted' ? 'var(--primary)' :
              geoPerm === 'denied'  ? 'var(--error)'   :
              'var(--text-2)'
            }
            title="Géolocalisation"
            subtitle={geoLoading ? 'Demande en cours…' : geo.text}
            subtitleVariant={geoLoading ? null : geo.variant}
            checked={geoPerm === 'granted'}
            onChange={handleGeoToggle}
            disabled={geoLoading || geoPerm === 'checking' || !('geolocation' in navigator)}
          />
        </div>

        {/* ── Mon espace (connecté) ── */}
        {user && (
          <div className="card" style={{ padding: 0, overflow: 'hidden', margin: 0 }}>
            <div className="profile-section-title">Mon espace</div>

            {isAdmin && (
              <>
                <button className="list-item" onClick={() => navigate('/admin')}>
                  <div className="list-item-left">
                    <div className="list-item-icon" style={{ background: 'rgba(99,102,241,.12)', color: '#818cf8' }}>
                      <Shield size={17} strokeWidth={2} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '.93rem' }}>Dashboard Admin</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-2)' }}>Gérer les magasins et les stats</div>
                    </div>
                  </div>
                  <ChevronRight size={17} style={{ color: 'var(--text-3)' }} />
                </button>
                <div className="divider" />
              </>
            )}

            {!isAdmin && (
              <>
                {isMerchant ? (
                  <button className="list-item" onClick={() => navigate('/merchant')}>
                    <div className="list-item-left">
                      <div className="list-item-icon" style={{ background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                        <Store size={17} strokeWidth={2} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '.93rem' }}>Gérer mon magasin</div>
                        <div style={{ fontSize: '.75rem', color: 'var(--text-2)' }}>Statut, horaires, informations</div>
                      </div>
                    </div>
                    <ChevronRight size={17} style={{ color: 'var(--text-3)' }} />
                  </button>
                ) : (
                  <button className="list-item" onClick={() => navigate('/register')}>
                    <div className="list-item-left">
                      <div className="list-item-icon" style={{ background: 'var(--primary-subtle)', color: 'var(--primary)' }}>
                        <Store size={17} strokeWidth={2} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '.93rem' }}>Devenir commerçant</div>
                        <div style={{ fontSize: '.75rem', color: 'var(--text-2)' }}>Créez votre page gratuitement</div>
                      </div>
                    </div>
                    <ChevronRight size={17} style={{ color: 'var(--text-3)' }} />
                  </button>
                )}
                <div className="divider" />
              </>
            )}

            <button className="list-item" onClick={() => navigate('/favorites')}>
              <div className="list-item-left">
                <div className="list-item-icon" style={{ background: 'rgba(99,102,241,.10)', color: '#6366f1' }}>
                  <Bookmark size={17} strokeWidth={2} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '.93rem' }}>Mes favoris</div>
                  <div style={{ fontSize: '.75rem', color: 'var(--text-2)' }}>Magasins sauvegardés</div>
                </div>
              </div>
              <ChevronRight size={17} style={{ color: 'var(--text-3)' }} />
            </button>
          </div>
        )}

        {/* ── Non connecté ── */}
        {!user && (
          <div className="card" style={{ margin: 0, textAlign: 'center', padding: '2rem 1.5rem' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: 'var(--bg)',
              boxShadow: 'var(--neu)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem', color: 'var(--text-3)',
            }}>
              <User size={26} strokeWidth={1.5} />
            </div>
            <div style={{ fontWeight: 800, marginBottom: '.4rem', fontSize: '1rem' }}>Non connecté</div>
            <div style={{ color: 'var(--text-2)', fontSize: '.85rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Connectez-vous pour gérer votre magasin.
            </div>
            <button className="btn btn-primary btn-large" onClick={() => navigate('/login')} style={{ marginBottom: '.6rem' }}>
              Se connecter
            </button>
            <button className="btn btn-ghost btn-large" onClick={() => navigate('/register')}>
              Créer un compte
            </button>
          </div>
        )}

        {/* ── Bouton déconnexion ── */}
        {user && (
          <button
            className="btn btn-danger btn-large"
            onClick={() => setShowLogout(true)}
            disabled={loggingOut}
            style={{ margin: 0 }}
          >
            <LogOut size={17} strokeWidth={2} />
            {loggingOut ? 'Déconnexion…' : 'Se déconnecter'}
          </button>
        )}
      </div>

      {/* ══════ MODAL DÉCONNEXION ══════ */}
      {showLogout && (
        <LogoutModal
          onConfirm={handleLogout}
          onCancel={() => setShowLogout(false)}
          loading={loggingOut}
        />
      )}

      {/* ══════ MODAL PERMISSION ══════ */}
      {permModal && (
        <PermissionModal
          type={permModal}
          onClose={() => setPermModal(null)}
        />
      )}

    </div>
  );
};

export default Profile;